import { Tooltip } from "@mui/material";
import { useContext, useMemo, MouseEventHandler, useState, useCallback } from "react";
import { gameSituationContext, GameSituationContextType } from "./GameSituationContext";
import { Asset, ComponentDataSource, PlayerEvent } from "../types";
import { GameComponentForGame, PlayerCounter, PlayerInfo } from "../implementationTypes";
import { TableContext, TableContextType } from "./tableContext";
import { initialSetup } from "../implementationGlobals";
import playerDisplayColors from "../playerColors";
import { isWebkit } from "./tableUtility";
import { UserInfo, UsernameDisplay } from "../pages/UserPage";
import { substituteIconsInText } from "../cardTextIcons";
import { ComponentComp } from "./GameComponent";
import { isModKeyDown } from "src/components/utils";

export function PlayerArea(props: {
  invitePwd?: string;
  selected?: string;
  color: string;
  y: number;
  x?: number;
  height: number;
  width: number;
  allPlayers: PlayerInfo[];
  counters: PlayerCounter[];
  userInfo: UserInfo;
  active: boolean;
  children: React.JSX.Element;
  customOrder?: any;
}) {
  let { switchPlayer, canInvite, editMode, connectedPlayers } = useContext(
    TableContext as React.Context<TableContextType>,
  );
  function getInviteLink(color: string) {
    let link = window.location.href;
    link += "?playAsColor=" + color;
    link += "&invitePwd=" + props.invitePwd;
    console.log(link);
    navigator.clipboard.writeText(link);
  }
  let selected = props.selected === props.color;
  let smallBoardScale = 0.5;
  //let styleColor = playerColors[props.color];
  let top = props.y;
  if (selected) {
    top += 100;
  } else {
    top += 1 * props.height - (-300 + props.height * smallBoardScale) * 0.5;
  }
  let left;
  if (props.x !== undefined) {
    left = props.x;
  }
  let style: { [key: string]: any } = {
    left,
    width: props.width + "px" || 0,
    height: props.height + "px",
    transform: "scale(" + (selected ? 1 : 0.4) + ")",
    zIndex: 5 + (selected ? 1 : 0),
  };
  let currOrder = props.allPlayers.findIndex((p) => p.color === props.color);
  let activeOrder = props.allPlayers.findIndex((p) => p.color === props.selected);
  let drawOrder = (currOrder - activeOrder + props.allPlayers.length) % props.allPlayers.length;
  style.top = top + "px";
  if (!selected) {
    let leftMult = drawOrder - 2;
    if (+props.allPlayers.length === 4) {
      if (+drawOrder === 2) {
        style.top = top + (160 + props.height * smallBoardScale * 0.5) + "px";
        leftMult = -0.5;
      }
      if (+drawOrder === 3) {
        leftMult = 0;
      }
    }
    //debugger;
    style.left = style.left + (props.width * 0.25 + leftMult * (props.width * 0.4 + 30)) + "px";
  }
  let counters = [];
  for (let counter of props.counters) {
    if (counter.options.parentCounter) {
      continue; // skip child counters here, they will be rendered in CounterComp
    }
    counters.push(
      <CounterComp
        value={counter.value ?? ""}
        name={counter.name}
        options={counter.options}
        playerColor={props.color}
        makeButtons={editMode || counter.options?.buttonsEnabled ? true : false}
        key={"counter-" + counter.name}
        childCounters={props.counters.filter((c) => c.options?.parentCounter === counter.name)}
      />,
    );
  }
  let inviteLink = <></>;
  if (canInvite) {
    inviteLink = (
      <button className="invite-button" onClick={() => getInviteLink(props.color)}>
        Get invite link
      </button>
    );
  }
  let nameWrap = (
    <div className="player-name-wrap">
      {props.customOrder ? +props.customOrder + 1 : +currOrder + 1}:{" "}
      <UsernameDisplay
        username={props.userInfo.username}
        badges={props.userInfo.badges}
        color="black"
        connected={connectedPlayers?.has(props.userInfo.username)}
      ></UsernameDisplay>
      {inviteLink}
    </div>
  );
  return (
    <div
      className={"player-area " + (selected ? "active" : "inactive") + (props.active ? " player-active" : "")}
      onClick={() => {
        console.log("child switching", props.color);
        switchPlayer(props.color);
      }}
      style={style}
    >
      {/*areaBg*/}
      <div
        className="player-overview"
        style={{
          background: playerDisplayColors[props.color],
          transform: selected ? "" : "scale(1.5) translate(90px, 0)",
          zIndex: 50,
        }}
      >
        {nameWrap}
        <div className="counter-wrap">{counters}</div>
      </div>
      {props.children}
    </div>
  );
}

export function CounterComp(props: {
  playerColor: string;
  value: number | string;
  name: string;
  childCounters?: PlayerCounter[];
  isChild?: boolean;
  options: {
    invisibleIfZero: boolean;
    displayAsComponents: boolean;
    clickable: boolean;
    marginRight?: number;
    buttonsEnabled?: boolean;
    maxValue?: number;
    customIcon?: string;
  };
  makeButtons: boolean;
}) {
  let { eventManager, evalClick, getImage, componentPool, situationScale } = useContext(
    TableContext as React.Context<TableContextType>,
  );
  let { componentClickCallBack } = useContext(gameSituationContext as React.Context<GameSituationContextType>);
  const incCounter = useCallback(
    (value: number) => {
      // TODO: multiple clicks buffering

      eventManager.newEvent("game move", {
        sourcePlayer: props.playerColor,
        value,
        name: props.name,
        color: props.playerColor,
        type: "inc counter",
      } as PlayerEvent);
    },
    [eventManager, props.name, props.playerColor],
  );
  const counterUp = useCallback(
    (evt: React.MouseEvent) => {
      console.log("counter up");
      incCounter(isModKeyDown(evt) ? 10 : 1);
    },
    [incCounter],
  );
  const counterDown = useCallback(
    (evt: React.MouseEvent) => {
      console.log("counter down");
      incCounter(isModKeyDown(evt) ? -10 : -1);
    },
    [incCounter],
  );
  let { globalAssets } = useContext(TableContext as React.Context<TableContextType>);

  if (+props.value === 0 && props.options?.invisibleIfZero) {
    return <></>;
  }
  let icon = globalAssets[props.options?.customIcon ?? props.name];
  let iconDiv = [];
  let labelDiv = <div className="counter-label">{props.name}: </div>;
  let maxValueText = "";
  if (props.options?.maxValue) {
    maxValueText = "/" + props.options.maxValue;
  }
  let valueDiv = (
    <b>
      {props.value}
      {maxValueText}
    </b>
  );

  let iconAmt = 1;
  if (props.options?.displayAsComponents) {
    iconAmt = props.value as number;
    valueDiv = <></>;
  }
  if (icon) {
    for (let i = 0; i < iconAmt; ++i) {
      iconDiv.push(
        <div
          className={"counter-icon icon-" + props.name + (props.options?.clickable ? " clickable" : "")}
          key={"counter-icon-" + props.name + "#" + i}
          onClick={(evt: React.MouseEvent) => {
            if (componentClickCallBack !== undefined) {
              componentClickCallBack(
                {
                  type: "counter",
                  name: props.name,
                  color: props.playerColor,
                  options: {
                    position: undefined,
                  },
                  id: "",
                  children: {},
                },
                evt,
              );
            }
          }}
          style={{
            backgroundImage: `url(${icon.url})`,
          }}
        ></div>,
      );
    }
    labelDiv = <></>;
  }
  let style: { [key: string]: any } = {};
  if (props.options?.marginRight) {
    style.marginRight = props.options.marginRight;
  }
  return (
    <div
      className={"counter" + (props.isChild ? " child-counter" : "")}
      style={style}
      id={"counter-" + props.playerColor + "-" + props.name}
    >
      {iconDiv}
      {labelDiv}
      {valueDiv}
      {props.childCounters?.length ? (
        <div className="child-counters">
          {props.childCounters.map((childCounter) => (
            <CounterComp key={childCounter.name} isChild={true} {...childCounter} />
          ))}
        </div>
      ) : null}
      <div className="buttonwrap">
        {props.makeButtons ? (
          <>
            <button onClick={counterDown}>-</button>
            <button onClick={counterUp}>+</button>
          </>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}

export function ClickableArea(props: {
  parentInfo: ComponentDataSource;
  parentData: GameComponentForGame;
  areaData: GameComponentForGame;
  name: string;
}) {
  let { componentClickCallBack, addGameIconsToText, displayTooltips } = useContext(
    gameSituationContext as React.Context<GameSituationContextType>,
  );
  let { customTooltip, isClickable, editMode } = useContext(TableContext as React.Context<TableContextType>);
  let data = Object.assign(
    {
      type: "clickableArea",
      parent: props.parentData.id,
      areaName: props.name,
    },
    props.areaData,
  );
  const clickable = useMemo(() => {
    return isClickable(data);
  }, [editMode, props.areaData]);
  let style = JSON.parse(JSON.stringify(props.areaData));
  if (!style.zIndex) {
    style.zIndex = props.parentInfo.zIndex ?? 0;
  }
  let areaData = {
    type: "clickableArea",
    name: props.name,
    parentData: props.parentData,
    options: {
      position: undefined,
    },
    id: "",
    children: {},
  };
  let tooltipContent: string | React.ReactNode = "";
  if (typeof customTooltip === "function" && displayTooltips) {
    try {
      tooltipContent = addGameIconsToText(customTooltip(areaData)) ?? "";
    } catch (error: any) {
      tooltipContent = "Error:" + error.message;
    }
  }
  return (
    <Tooltip
      key={"clikable-area-" + props.name}
      title={tooltipContent}
      enterDelay={1500}
      enterNextDelay={1500}
      arrow
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 15],
              },
            },
          ],
        },
      }}
    >
      <div
        className={"clickable-area" + (clickable ? " active " + clickable : "")}
        data-clickable-name={props.name}
        style={style}
        onClick={componentClickCallBack?.bind(null, areaData)}
      ></div>
    </Tooltip>
  );
}

export function ComponentPosition(props: {
  parentScale: number;
  x: number;
  y: number;
  posData: any;
  parentId: string;
  name: string;
  index: string | number;
  parentData: GameComponentForGame;
  parentInfo: ComponentDataSource;
  logPreview: boolean;
}) {
  let { customTooltip, tableState, adjustedGameState, editMode, cheatMode, isPosVisible } = useContext(
    TableContext as React.Context<TableContextType>,
  );
  let { componentClickCallBack, selectedComponent, bankReveal, positionClickCallBack } = useContext(
    gameSituationContext as React.Context<GameSituationContextType>,
  );
  let movingNames: string[] = [];
  if (bankReveal) {
    if (!bankReveal.options) {
      debugger;
    }
    movingNames = bankReveal.options.contentNames;
    if (!movingNames) {
      movingNames = ["__card-" + bankReveal.deckName];
    }
  }
  if (selectedComponent !== undefined && selectedComponent) {
    movingNames = [selectedComponent?.name ?? ""];
  }
  let positions = props.parentInfo.positions[props.name];
  let isPositionClickable: boolean = movingNames.filter((a) => (positions.accepts || []).includes(a)).length > 0;
  let posVisible = positions.multi || !props.posData || !props.posData[props.index];
  initialSetup(tableState);
  let positionName = props.name;
  let posVisibleFunction = adjustedGameState(tableState.getActiveState()).isPosVisible ?? isPosVisible;
  let positionInfo = useMemo(() => {
    try {
      return (
        (typeof posVisibleFunction == "function" &&
          !editMode &&
          posVisibleFunction(
            { positionIndex: props.index, positionName },
            props.parentData,
            tableState,
            selectedComponent ?? bankReveal,
          )) ||
        (typeof isPosVisible == "function" &&
          !editMode &&
          isPosVisible(
            { positionIndex: props.index, positionName },
            props.parentData,
            tableState,
            selectedComponent ?? bankReveal,
          ))
      );
    } catch (err) {
      return false;
    }
  }, [tableState, editMode, props.parentData, selectedComponent, bankReveal]);

  if (isPosVisible && !positionInfo) {
    posVisible = false;
  }
  if (isPosVisible && positionInfo) {
    posVisible = true;
  }
  if (movingNames.length === 0) {
    isPositionClickable = posVisible; // if no component is selected, override with result of isPosVisible
  }
  if (editMode || cheatMode) {
    posVisible = true;
  }
  let { addGameIconsToText } = useContext(gameSituationContext as React.Context<GameSituationContextType>);
  let textDisplay = positionInfo?.customDisplay;
  if (editMode) {
    textDisplay = props.index;
  }
  let domId = "position-" + props.parentId + "-" + props.name + "-" + props.index;
  let tooltipContent: string | React.ReactNode = useMemo(() => {
    if (editMode) {
      return "";
    }
    if (typeof customTooltip === "function") {
      let result;
      try {
        result =
          addGameIconsToText(
            customTooltip({
              type: "positionDisplay",
              name: props.name,
              id: domId,
              options: {
                positionIndex: props.index,
              },
              parent: props.parentId,
              children: {},
            }),
          ) ?? "";
      } catch (error: any) {
        result = "Error:" + error.message;
      }
      return result;
    }
  }, [tableState, editMode]);
  if ((!isPositionClickable || !posVisible) && !tooltipContent) {
    return (
      <div
        className="position-stub"
        id={domId}
        style={{
          left: props.x + "%",
          top: props.y + "%",
        }}
      ></div>
    );
  }
  return (
    <Tooltip
      enterDelay={1500}
      arrow
      enterNextDelay={1500}
      title={tooltipContent}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 15],
              },
            },
          ],
        },
      }}
    >
      <div
        style={{
          transform: "scale(" + (1 / props.parentScale) * (positionInfo?.scaleMult ?? 1) + ")",
          left: props.x + "%",
          top: props.y + "%",
          zIndex: editMode ? 1500 : 1500,
          position: "absolute",
        }}
      >
        <div
          className={
            "cgo-position" +
            (posVisible ? "" : " invisible") +
            (positionInfo?.hidden && !editMode ? " hidden" : "") +
            (editMode ? " editmode" : "") +
            (positionInfo?.outline ? " outline" : "")
          }
          style={{
            opacity: posVisible && isPositionClickable && (!positionInfo?.hidden || editMode) ? "1" : "0",
            pointerEvents: (posVisible && isPositionClickable) || tooltipContent !== "" ? "all" : "none",
            background: positionInfo?.color ?? "",
            animation:
              isWebkit() || !isPositionClickable || !posVisible || positionInfo?.hidden
                ? "none"
                : "position-blink 1.4s infinite alternate, position-fadein 0.5s",
          }}
          id={domId}
          title={editMode ? props.name + ":" + props.index : undefined}
          onClick={
            isPositionClickable && !props.logPreview
              ? positionClickCallBack.bind(null, props.parentData.id, props.index, positionName, posVisible)
              : () => null
          }
        >
          {positionInfo?.displayIndex ? props.index : ""}
          <div className="position-custom-display">{textDisplay}</div>
        </div>
      </div>
    </Tooltip>
  );
}

export function CardWrap(props: {
  cardBackImage?: string;
  clipPath?: string;
  flipped?: boolean;
  style?: { [key: string]: any };
  cardFront?: React.JSX.Element;
  cardRev?: React.JSX.Element;
  children?: React.JSX.Element[];
  borderRadius?: string;
}) {
  let dimensionStyle = {
    width: "100%",
    height: "100%",
  };

  Object.assign(props.cardFront?.props?.style?.borderRadius ?? {}, {
    borderRadius: props.borderRadius,
  });

  let clipPathStyle = {
    clipPath: props.clipPath,
  };

  return (
    <div className="card-game-wrap" style={Object.assign({}, dimensionStyle, props.style)}>
      <div className={"card-rotate-wrap"}>
        <div className={"card-flip-wrap" + (props.flipped ? " flipped" : "")}>
          <div className="card-rev" style={Object.assign({}, clipPathStyle)}>
            {props.cardRev}
          </div>
          <div className="card-front" style={Object.assign({}, clipPathStyle)}>
            {props.cardFront}
          </div>
          {/*props.children*/}
        </div>
      </div>
    </div>
  );
}
export function BankComp(props: { name: string; canMove?: boolean; clickCallback?: MouseEventHandler }) {
  let additionalClass = "";
  if (props.canMove) {
    additionalClass += "can-put";
  }
  return (
    <div className={"bank-wrap " + additionalClass}>
      <div className="bank">Bank {props.name}</div>
      <button onClick={props.clickCallback}>Take component</button>
    </div>
  );
}

export function DeckComp(props: {
  discardAmt?: number;
  componentAmt: number;
  scaleMult?: number;
  cardBack?: string;
  height?: number;
  width?: number;
  data: GameComponentForGame;
  topCard?: GameComponentForGame;
  deckName?: string;
}) {
  let { editMode, decks, setOpenDeckContentId } = useContext(TableContext as React.Context<TableContextType>);
  /*let cards = [];

  for (let i = 0; i < props.componentAmt; ++i) {
    cards.push(
      <div
        style={{
          width: "280px",
          height: "400px",
          transform: "scale(" + props.scaleMult + ")",
          transformOrigin: "top left",
          position: "absolute",
        }}
        key={"card-" + i}
      >
        <CardWrap cardBackImage={props.cardBack} flipped={true} />
      </div>
    );
  }*/
  let deckInfo = decks[props.topCard?.options.deckName ?? ""];

  return (
    <div
      className={"deck-wrap"}
      style={{
        width: (props.height ?? 0) * 0.7 + "px",
        height: props.height + "px",
        backgroundImage: props.componentAmt > 0 ? props.cardBack : "",
        backgroundColor: props.componentAmt > 0 && !props.cardBack && !props.topCard ? "gray" : "",
      }}
      onContextMenu={(evt) => {
        setOpenDeckContentId(props.data.id);
        evt.stopPropagation();
        evt.preventDefault();
      }}
    >
      <div className="deck">
        <span key={"deck-label"} className="deck-label">
          {editMode || props.data.options.label ? (props.data.options.label ?? props.data.name) : ""}
        </span>
        {props.topCard ? (
          <div
            className="deck-topcard-wrap"
            style={{
              transform: "scale(" + 133 / deckInfo?.cardInfo.w + ")",
              transformOrigin: "center",
              position: "absolute",
              top: "0",
              left: "0",
              width: "100%",
              height: "100%",
              zIndex: 0,
            }}
          >
            <ComponentComp data={props.topCard} x={50} y={50} clickable={false} deckTopCard={true}></ComponentComp>
          </div>
        ) : (
          <></>
        )}
        {/*cards*/}
      </div>
      <span
        className={"deck-amt" + (props.topCard ? " amt-topcard" : "")}
        style={{
          fontSize: Math.floor((props.height ?? 0) * 0.5 * (props.topCard ? 0.6 : 1)) + "px",
        }}
      >
        {props.componentAmt}
      </span>
      {(props.discardAmt ?? 0) > 0 && (
        <span
          className="discard-amt"
          style={{
            fontSize: Math.floor((props.height ?? 0) * 0.3) + "px",
          }}
        >
          {props.discardAmt}
          <span
            className="discard-icon"
            style={{
              width: (props.height ?? 0) * 0.2 + "px",
              height: (props.height ?? 0) * 0.2 + "px",
            }}
          ></span>
        </span>
      )}
    </div>
  );
}

export function LabelDisplayComp(props: {
  options: {
    [key: string]: any;
  };
  name: string;
  value: any;
}) {
  let {
    getComponentInfo,
    setEditMode: _setEditMode,
    getImage,
    globalAssets,
    playerColors,
  } = useContext(TableContext as React.Context<TableContextType>);
  let [previousValue, setPreviousValue] = useState();
  let [physicalValue, setPhysicalValue] = useState();
  let [isPulsing, setIsPulsing] = useState(false);
  let style = { ...(props.options.style ?? {}) };
  let displayInfo = getComponentInfo({
    name: "global-display-" + props.name,
    options: props.options,
    children: {},
    id: "dummy",
  });
  Object.assign(style, displayInfo.style);
  if (props.value !== previousValue) {
    setPreviousValue(props.value);
    if (props.options.pulseOnChange) {
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), 1000);
      setTimeout(() => setPhysicalValue(props.value), 300);
    } else {
      setPhysicalValue(props.value);
    }
  }
  if (isPulsing) {
    if (!style.transform) {
      style.transform = "";
    }
    style.transform += " scale(1.2, 1.2)";
  }
  if (props.options.fontSize) {
    style.fontSize = props.options.fontSize; // this is just for backwards compatibility really
  }
  if (props.options.targetPlayer === "white") {
    style.color = "black";
  }
  let valueNameDisplay = <></>;
  let targetDisplay = props.options.displayVariableName === true ? props.name : props.options.displayVariableName;

  let iconHeight = parseInt(props.options.style?.fontSize ?? props.options.fontSize ?? 20);
  if (globalAssets[targetDisplay]) {
    valueNameDisplay = (
      <img
        src={globalAssets[targetDisplay].url}
        className={"global-icon icon-" + targetDisplay + (props.options?.clickable ? " clickable" : "")}
        style={{
          height: iconHeight + "px",
        }}
      ></img>
    );
  } else if (targetDisplay) {
    valueNameDisplay = <span>{targetDisplay + ": "}</span>;
  }
  let newIcons: { [key: string]: Asset } = {};
  for (let name in globalAssets) {
    newIcons[name] = { url: globalAssets[name].url, aspect: 1 };
  }

  const substituteText = useCallback(
    (value: string) => {
      if (props.options.substitutePlayerName) {
        // find a player with color that matches `value`
        const playerInfo = playerColors?.find((info) => info.color === value);
        return playerInfo?.userInfo?.username ?? value;
      } else {
        return substituteIconsInText(value, iconHeight, newIcons);
      }
    },
    [props.options.substitutePlayerName, playerColors, iconHeight, newIcons],
  );

  return (
    <div className="variable-display" style={style}>
      {valueNameDisplay}
      <span>{physicalValue !== undefined ? substituteText("" + physicalValue) : ""}</span>
    </div>
  );
}
